import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  Timestamp,
  updateDoc,
  where,
  writeBatch,
  type DocumentReference,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import type { PartnerDebtDraft, PartnerDebtDraftLine } from '../types/coffeeInventory';
import type { WarehouseGoodsItem } from '../types/warehouseGoodsCatalog';
import { warehouseGoodsCatalogService } from './warehouseGoodsCatalogService';

const DRAFT_COLLECTION = 'coffee-partner-debt-drafts';
const TRANSACTION_COLLECTION = 'coffee-stock-transactions';
const WAREHOUSE_COLLECTION = 'warehouse-entries';
const PARTNER_COLLECTION = 'coffee-partners';

const toDateValue = (value: any): Date => {
  if (!value) return new Date();
  if (value instanceof Date) return value;
  if (value?.toDate) return value.toDate();
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? new Date() : parsed;
};

const normalizeNumber = (value: number | string | undefined): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const normalizeText = (value?: string) => String(value || '').trim().toLowerCase();

const clampDiscount = (value: number) => Math.max(0, Math.min(value, 100));

const computeWarehouseTotalAmount = (quantity: number, unitPrice: number): number => {
  return (quantity / 1000) * unitPrice;
};

/** Giữ nguyên coffeeTypeId (id Firestore danh mục) — không lowercase để khớp kho & giao dịch. */
const normalizeLine = (raw: any): PartnerDebtDraftLine | null => {
  const coffeeTypeId = String(raw?.coffeeTypeId || '').trim();
  const coffeeTypeName = String(raw?.coffeeTypeName || '').trim();
  const quantity = normalizeNumber(raw?.quantity);
  const unitPricePerKg = normalizeNumber(raw?.unitPricePerKg);
  if (!coffeeTypeId || !coffeeTypeName || quantity <= 0 || unitPricePerKg <= 0) return null;
  return { coffeeTypeId, coffeeTypeName, quantity, unitPricePerKg };
};

const normalizeDraft = (raw: any): PartnerDebtDraft => {
  const lines = Array.isArray(raw.lines) ? raw.lines.map(normalizeLine).filter(Boolean) as PartnerDebtDraftLine[] : [];
  const issueDateRaw = raw.issueDate ? toDateValue(raw.issueDate) : undefined;
  const createdAtVal = raw.createdAt ? toDateValue(raw.createdAt) : undefined;
  return {
    id: raw.id,
    partnerId: String(raw.partnerId || '').trim(),
    partnerName: String(raw.partnerName || '').trim(),
    issueDate: issueDateRaw || createdAtVal,
    discountRate: clampDiscount(normalizeNumber(raw.discountRate)),
    lines,
    status: raw.status === 'finalized' ? 'finalized' : 'draft',
    notes: String(raw.notes || ''),
    finalizedAt: raw.finalizedAt ? toDateValue(raw.finalizedAt) : undefined,
    finalizedInvoiceDate: raw.finalizedInvoiceDate ? toDateValue(raw.finalizedInvoiceDate) : undefined,
    finalizedStoreName: String(raw.finalizedStoreName || '').trim() || undefined,
    finalizedTransactionIds: Array.isArray(raw.finalizedTransactionIds)
      ? raw.finalizedTransactionIds.map((id: any) => String(id || '').trim()).filter(Boolean)
      : undefined,
    createdAt: raw.createdAt ? toDateValue(raw.createdAt) : undefined,
    updatedAt: raw.updatedAt ? toDateValue(raw.updatedAt) : undefined,
  };
};

/** Ghép dòng kho với danh mục hàng hóa để cùng một id dù kho lưu id doc hay mã/tên. */
function findCatalogGood(
  catalog: WarehouseGoodsItem[],
  rawId?: string,
  rawName?: string
): WarehouseGoodsItem | undefined {
  const s = String(rawId || '').trim();
  const name = String(rawName || '').trim();
  if (s) {
    const byId = catalog.find((g) => g.id === s);
    if (byId) return byId;
    const byNormId = catalog.find((g) => !!(g.id && normalizeText(g.id) === normalizeText(s)));
    if (byNormId) return byNormId;
    const byCode = catalog.find((g) => g.productCode && normalizeText(g.productCode) === normalizeText(s));
    if (byCode) return byCode;
  }
  if (name) {
    return catalog.find((g) => normalizeText(g.name) === normalizeText(name));
  }
  return undefined;
}

export const partnerDebtDraftService = {
  /** Tất cả phiếu công nợ (nháp + đã xuất HĐ), sắp xếp theo ngày nhập phiếu mới trước. */
  async getAllDrafts(): Promise<PartnerDebtDraft[]> {
    const snapshot = await getDocs(collection(db, DRAFT_COLLECTION));
    const rows = snapshot.docs.map((d) => normalizeDraft({ id: d.id, ...d.data() }));
    return rows.sort((a, b) => {
      const ta = (a.issueDate || a.createdAt || new Date(0)).getTime();
      const tb = (b.issueDate || b.createdAt || new Date(0)).getTime();
      if (tb !== ta) return tb - ta;
      return toDateValue(b.updatedAt).getTime() - toDateValue(a.updatedAt).getTime();
    });
  },

  async addDraft(
    data: Omit<PartnerDebtDraft, 'id' | 'status' | 'createdAt' | 'updatedAt' | 'finalizedAt' | 'finalizedTransactionIds'>
  ): Promise<PartnerDebtDraft> {
    const lines = (data.lines || []).map(normalizeLine).filter(Boolean) as PartnerDebtDraftLine[];
    const now = Timestamp.fromDate(new Date());
    const issueTs = data.issueDate ? Timestamp.fromDate(toDateValue(data.issueDate)) : now;
    const payload = {
      partnerId: String(data.partnerId || '').trim(),
      partnerName: String(data.partnerName || '').trim(),
      issueDate: issueTs,
      discountRate: clampDiscount(normalizeNumber(data.discountRate)),
      lines,
      status: 'draft' as const,
      notes: String(data.notes || '').trim(),
      createdAt: now,
      updatedAt: now,
    };
    const docRef = await addDoc(collection(db, DRAFT_COLLECTION), payload);
    return normalizeDraft({ id: docRef.id, ...payload });
  },

  async updateDraft(
    id: string,
    data: Partial<
      Pick<PartnerDebtDraft, 'partnerId' | 'partnerName' | 'issueDate' | 'discountRate' | 'lines' | 'notes'>
    >
  ): Promise<void> {
    const ref = doc(db, DRAFT_COLLECTION, id);
    const existing = await getDoc(ref);
    if (!existing.exists()) {
      throw new Error('Không tìm thấy phiếu.');
    }
    const cur = normalizeDraft({ id: existing.id, ...existing.data() });
    if (cur.status === 'finalized') {
      throw new Error('Phiếu đã xuất HĐ, không thể chỉnh sửa.');
    }
    const payload: Record<string, any> = {
      updatedAt: Timestamp.fromDate(new Date()),
    };
    if (data.partnerId !== undefined) payload.partnerId = String(data.partnerId || '').trim();
    if (data.partnerName !== undefined) payload.partnerName = String(data.partnerName || '').trim();
    if (data.issueDate !== undefined) {
      payload.issueDate = Timestamp.fromDate(toDateValue(data.issueDate));
    }
    if (data.discountRate !== undefined) payload.discountRate = clampDiscount(normalizeNumber(data.discountRate));
    if (data.notes !== undefined) payload.notes = String(data.notes || '').trim();
    if (data.lines !== undefined) {
      payload.lines = (data.lines || []).map(normalizeLine).filter(Boolean);
    }
    await updateDoc(ref, payload);
  },

  /**
   * Xóa phiếu. Nếu đã xuất HĐ: xóa các dòng kho xuất đối tác + giao dịch partner_output tương ứng
   * (tổng tồn kho tổng khôi phục như trước khi chốt). Phiếu nháp chỉ xóa bản ghi phiếu.
   */
  async deleteDraft(id: string): Promise<void> {
    const ref = doc(db, DRAFT_COLLECTION, id);
    const snap = await getDoc(ref);
    if (!snap.exists()) {
      throw new Error('Không tìm thấy phiếu.');
    }
    const draft = normalizeDraft({ id: snap.id, ...snap.data() });

    if (draft.status !== 'finalized') {
      await deleteDoc(ref);
      return;
    }

    const txIds = draft.finalizedTransactionIds || [];
    if (txIds.length === 0) {
      throw new Error(
        'Phiếu đã xuất HĐ nhưng không có danh sách giao dịch để hoàn kho. Không xóa phiếu — vui lòng liên hệ quản trị.'
      );
    }

    const toDelete: DocumentReference[] = [];
    const seenPaths = new Set<string>();
    const pushRef = (r: DocumentReference) => {
      const p = r.path;
      if (seenPaths.has(p)) return;
      seenPaths.add(p);
      toDelete.push(r);
    };

    for (const txId of txIds) {
      const whSnap = await getDocs(
        query(collection(db, WAREHOUSE_COLLECTION), where('partnerTransactionId', '==', txId))
      );
      whSnap.docs.forEach((d) => pushRef(d.ref));
      pushRef(doc(db, TRANSACTION_COLLECTION, txId));
    }

    pushRef(ref);

    const chunkSize = 450;
    for (let i = 0; i < toDelete.length; i += chunkSize) {
      const batch = writeBatch(db);
      const chunk = toDelete.slice(i, i + chunkSize);
      chunk.forEach((r) => batch.delete(r));
      await batch.commit();
    }
  },

  /**
   * Ghi nhận hàng loạt partner_output + xuất kho đối tác, đánh dấu phiếu finalized (một batch).
   */
  async finalizeDraft(
    draftId: string,
    input: { date: Date; /** Mặc định "Kho tổng" nếu bỏ trống */ storeName?: string }
  ): Promise<{ transactionIds: string[] }> {
    const draftRef = doc(db, DRAFT_COLLECTION, draftId);
    const draftSnap = await getDoc(draftRef);
    if (!draftSnap.exists()) {
      throw new Error('Không tìm thấy phiếu công nợ.');
    }
    const draft = normalizeDraft({ id: draftSnap.id, ...draftSnap.data() });
    if (draft.status !== 'draft') {
      throw new Error('Phiếu này đã được chốt trước đó.');
    }
    if (!draft.lines.length) {
      throw new Error('Phiếu cần ít nhất một dòng hạt cà phê.');
    }
    if (!String(draft.partnerId || '').trim()) {
      throw new Error('Thiếu đối tác trên phiếu.');
    }

    const storeName = String(input.storeName || '').trim() || 'Kho tổng';

    const txDate = toDateValue(input.date);
    if (Number.isNaN(txDate.getTime())) {
      throw new Error('Ngày giao dịch không hợp lệ.');
    }

    const discountRate = clampDiscount(draft.discountRate);
    const [catalog] = await Promise.all([warehouseGoodsCatalogService.getAll()]);

    let partnerName = draft.partnerName;
    const partnerSnap = await getDoc(doc(db, PARTNER_COLLECTION, draft.partnerId));
    if (partnerSnap.exists()) {
      const latest = String(partnerSnap.data()?.name || '').trim();
      if (latest) partnerName = latest;
    }

    /** Bỏ kiểm tra tồn kho (môi trường test — cho phép xuất HĐ dù vượt tồn). */

    const month = `${txDate.getFullYear()}-${String(txDate.getMonth() + 1).padStart(2, '0')}`;
    const nowTs = Timestamp.fromDate(new Date());
    const dateTs = Timestamp.fromDate(txDate);
    const notes = String(draft.notes || '').trim();

    const batch = writeBatch(db);
    const transactionIds: string[] = [];

    for (const line of draft.lines) {
      const g = findCatalogGood(catalog || [], line.coffeeTypeId, line.coffeeTypeName);
      const coffeeTypeId = g?.id || String(line.coffeeTypeId || '').trim();
      const coffeeTypeName = String(g?.name || line.coffeeTypeName || '').trim();

      const quantityGram = normalizeNumber(line.quantity);
      const unitPricePerKg = normalizeNumber(line.unitPricePerKg);
      const unitPricePerGram = unitPricePerKg / 1000;
      const totalAmount = quantityGram * unitPricePerGram;
      const discountedAmount = totalAmount * (1 - discountRate / 100);

      const txRef = doc(collection(db, TRANSACTION_COLLECTION));
      transactionIds.push(txRef.id);

      batch.set(txRef, {
        date: dateTs,
        transactionType: 'partner_output',
        coffeeTypeId,
        coffeeTypeName,
        unit: 'g',
        quantity: quantityGram,
        unitPrice: unitPricePerGram,
        totalAmount,
        partnerId: draft.partnerId,
        partnerName,
        discountRate,
        discountedAmount,
        storeName,
        notes,
        createdAt: nowTs,
        updatedAt: nowTs,
      });

      const qtyNeg = -Math.abs(quantityGram);
      const whRef = doc(collection(db, WAREHOUSE_COLLECTION));
      const totalAmountWh = computeWarehouseTotalAmount(qtyNeg, unitPricePerKg);

      batch.set(whRef, {
        month,
        coffeeTypeId,
        coffeeTypeName,
        unit: 'g',
        quantity: qtyNeg,
        unitPrice: unitPricePerKg,
        totalAmount: totalAmountWh,
        amountMode: 'per1000g',
        entryType: 'export',
        exportTargetType: 'partner',
        partnerId: draft.partnerId,
        partnerName,
        partnerTransactionId: txRef.id,
        notes,
        createdAt: nowTs,
        updatedAt: nowTs,
      });
    }

    batch.update(draftRef, {
      status: 'finalized',
      finalizedAt: nowTs,
      finalizedInvoiceDate: dateTs,
      finalizedStoreName: storeName,
      finalizedTransactionIds: transactionIds,
      updatedAt: nowTs,
    });

    await batch.commit();
    return { transactionIds };
  },
};
